import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import {
  Search, Mail, Send, Users, Globe, Trash2, RefreshCw, Upload, Plus, Eye, MousePointerClick,
  AlertTriangle, Filter, ExternalLink, FileText, X, CheckCircle2, MailX, ChevronRight,
} from "lucide-react";
import { toast } from "sonner";

const MODES = [
  { key: "leads", label: "Leads", icon: Users },
  { key: "campaigns", label: "Campaigns", icon: Mail },
  { key: "suppression", label: "Unsubscribed", icon: MailX },
];

function fmtDate(iso) {
  return iso ? new Date(iso).toLocaleString("en-NZ", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
}

export default function CampaignsPanel() {
  const [mode, setMode] = useState("leads");
  return (
    <div className="space-y-6" data-testid="campaigns-panel">
      {/* Sub-tabs */}
      <div className="inline-flex gap-1 bg-zinc-100 p-1 rounded-sm" data-testid="campaigns-sub-tabs">
        {MODES.map((m) => {
          const Icon = m.icon;
          return (
            <button
              key={m.key}
              onClick={() => setMode(m.key)}
              className={`px-4 py-2 text-xs font-bold uppercase tracking-wider flex items-center gap-2 transition-colors ${
                mode === m.key ? "bg-[#09090B] text-white" : "text-zinc-600 hover:text-zinc-900"
              }`}
              data-testid={`campaigns-mode-${m.key}`}>
              <Icon className="w-3.5 h-3.5" />
              {m.label}
            </button>
          );
        })}
      </div>

      {mode === "leads" && <LeadsTab />}
      {mode === "campaigns" && <CampaignsTab />}
      {mode === "suppression" && <SuppressionTab />}
    </div>
  );
}

/* ============================================================
   LEADS TAB
   ============================================================ */
function LeadsTab() {
  const [leads, setLeads] = useState([]);
  const [total, setTotal] = useState(0);
  const [withEmails, setWithEmails] = useState(0);
  const [filter, setFilter] = useState({ status: "", with_email: "", q: "" });
  const [loading, setLoading] = useState(false);
  const [enriching, setEnriching] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [importOpen, setImportOpen] = useState(false);
  const [manualOpen, setManualOpen] = useState(false);
  const [selected, setSelected] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const params = {};
      if (filter.status) params.status = filter.status;
      if (filter.with_email !== "") params.with_email = filter.with_email;
      if (filter.q) params.q = filter.q;
      const r = await api.get("/campaigns/leads", { params });
      setLeads(r.data.items || []);
      setTotal(r.data.total || 0);
      setWithEmails(r.data.with_emails || 0);
    } catch (e) {
      toast.error("Failed to load leads");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [filter.status, filter.with_email]);

  const enrichAll = async () => {
    if (!confirm("Scan all leads without emails for contact addresses? This may take a minute.")) return;
    setEnriching(true);
    try {
      const r = await api.post("/campaigns/leads/enrich-all", null, { params: { limit: 100 } });
      toast.success(`Scanned ${r.data.scanned}, found emails on ${r.data.found_emails}`);
      await load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Enrich failed");
    } finally {
      setEnriching(false);
    }
  };

  const deleteSelected = async () => {
    const ids = Object.keys(selected).filter((k) => selected[k]);
    if (!ids.length) return;
    if (!confirm(`Delete ${ids.length} lead${ids.length === 1 ? "" : "s"}?`)) return;
    try {
      await api.post("/campaigns/leads/delete-bulk", { ids });
      toast.success(`Deleted ${ids.length}`);
      setSelected({});
      load();
    } catch (e) {
      toast.error("Delete failed");
    }
  };

  const enrichOne = async (id) => {
    try {
      const r = await api.post(`/campaigns/leads/${id}/enrich`);
      toast.success(r.data.emails?.length ? `Found ${r.data.emails.length} email(s)` : "No emails found");
      load();
    } catch (e) {
      toast.error("Enrich failed");
    }
  };

  const deleteOne = async (id) => {
    if (!confirm("Delete this lead?")) return;
    await api.delete(`/campaigns/leads/${id}`);
    load();
  };

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const allSelected = leads.length > 0 && selectedCount === leads.length;

  return (
    <div className="space-y-4">
      {/* Stats */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatTile label="Total leads" value={total} />
        <StatTile label="With email" value={withEmails} color="green" />
        <StatTile label="Missing email" value={Math.max(0, total - withEmails)} color="orange" />
        <StatTile label="Filtered" value={leads.length} />
      </div>

      {/* Toolbar */}
      <div className="flex flex-wrap gap-2 items-center">
        <Button onClick={() => setSearchOpen(true)} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-search-google-btn">
          <Search className="w-3.5 h-3.5 mr-2" /> Search Google
        </Button>
        <Button onClick={() => setManualOpen(true)} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-add-manual-btn">
          <Plus className="w-3.5 h-3.5 mr-2" /> Add lead
        </Button>
        <Button onClick={() => setImportOpen(true)} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-import-csv-btn">
          <Upload className="w-3.5 h-3.5 mr-2" /> Import CSV
        </Button>
        <Button onClick={enrichAll} variant="outline" disabled={enriching} className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-enrich-all-btn">
          <Globe className="w-3.5 h-3.5 mr-2" /> {enriching ? "Scanning…" : "Find emails"}
        </Button>
        {selectedCount > 0 && (
          <Button onClick={deleteSelected} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold text-red-600 border-red-300" data-testid="campaigns-delete-selected-btn">
            <Trash2 className="w-3.5 h-3.5 mr-2" /> Delete {selectedCount}
          </Button>
        )}
        <div className="flex-1" />
        <div className="flex items-center gap-2">
          <Input
            placeholder="Search…"
            value={filter.q}
            onChange={(e) => setFilter({ ...filter, q: e.target.value })}
            onKeyDown={(e) => { if (e.key === "Enter") load(); }}
            className="h-9 w-44 rounded-sm border-zinc-400 text-xs"
            data-testid="campaigns-leads-search"
          />
          <select
            value={filter.with_email}
            onChange={(e) => setFilter({ ...filter, with_email: e.target.value })}
            className="h-9 rounded-sm border border-zinc-400 px-2 text-xs"
            data-testid="campaigns-leads-filter-email">
            <option value="">All</option>
            <option value="true">With email</option>
            <option value="false">Missing email</option>
          </select>
          <select
            value={filter.status}
            onChange={(e) => setFilter({ ...filter, status: e.target.value })}
            className="h-9 rounded-sm border border-zinc-400 px-2 text-xs"
            data-testid="campaigns-leads-filter-status">
            <option value="">All status</option>
            <option value="new">New</option>
            <option value="contacted">Contacted</option>
            <option value="converted">Converted</option>
            <option value="do-not-contact">Do not contact</option>
          </select>
        </div>
      </div>

      {/* Table */}
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaigns-leads-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2 w-8">
                <input type="checkbox" checked={allSelected} onChange={(e) => {
                  const next = {};
                  if (e.target.checked) leads.forEach((l) => { next[l.id] = true; });
                  setSelected(next);
                }} />
              </th>
              <th className="px-3 py-2">Business</th>
              <th className="px-3 py-2">Emails</th>
              <th className="px-3 py-2 hidden md:table-cell">Phone</th>
              <th className="px-3 py-2 hidden lg:table-cell">Source</th>
              <th className="px-3 py-2">Status</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={7} className="px-3 py-6 text-center text-zinc-500">Loading…</td></tr>
            )}
            {!loading && leads.length === 0 && (
              <tr><td colSpan={7} className="px-3 py-10 text-center text-zinc-500">
                No leads yet. Try <b>Search Google</b> or <b>Import CSV</b>.
              </td></tr>
            )}
            {leads.map((lead) => (
              <tr key={lead.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`campaigns-lead-row-${lead.id}`}>
                <td className="px-3 py-2">
                  <input type="checkbox" checked={!!selected[lead.id]} onChange={(e) => setSelected({ ...selected, [lead.id]: e.target.checked })} />
                </td>
                <td className="px-3 py-2">
                  <div className="font-bold">{lead.name}</div>
                  {lead.address && <div className="text-[11px] text-zinc-500 mt-0.5">{lead.address}</div>}
                  {lead.website && (
                    <a href={lead.website} target="_blank" rel="noreferrer" className="text-[11px] text-[#FF5F15] hover:underline inline-flex items-center gap-1 mt-0.5">
                      <Globe className="w-3 h-3" /> {lead.website.replace(/^https?:\/\//, "").slice(0, 40)}
                    </a>
                  )}
                </td>
                <td className="px-3 py-2">
                  {lead.emails?.length ? (
                    <div className="flex flex-wrap gap-1">
                      {lead.emails.map((e) => (
                        <span key={e} className="inline-flex items-center gap-1 bg-green-50 border border-green-300 text-green-900 text-[10px] font-mono px-1.5 py-0.5 rounded-sm">
                          <Mail className="w-3 h-3" /> {e}
                        </span>
                      ))}
                    </div>
                  ) : (
                    <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-400">—</span>
                  )}
                </td>
                <td className="px-3 py-2 hidden md:table-cell text-xs text-zinc-600">{lead.phone || "—"}</td>
                <td className="px-3 py-2 hidden lg:table-cell">
                  <span className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{lead.source}</span>
                </td>
                <td className="px-3 py-2">
                  <StatusPill status={lead.status} />
                </td>
                <td className="px-3 py-2 text-right">
                  <div className="inline-flex items-center gap-1">
                    {lead.website && (!lead.emails?.length) && (
                      <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => enrichOne(lead.id)} title="Find emails" data-testid={`campaigns-lead-enrich-${lead.id}`}>
                        <Globe className="w-3.5 h-3.5" />
                      </Button>
                    )}
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => deleteOne(lead.id)} title="Delete" data-testid={`campaigns-lead-delete-${lead.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {searchOpen && <SearchGoogleDialog onClose={() => setSearchOpen(false)} onSaved={load} />}
      {manualOpen && <ManualLeadDialog onClose={() => setManualOpen(false)} onSaved={load} />}
      {importOpen && <ImportCsvDialog onClose={() => setImportOpen(false)} onSaved={load} />}
    </div>
  );
}

function StatTile({ label, value, color }) {
  const colorClass = color === "green" ? "border-l-green-500" : color === "orange" ? "border-l-[#FF5F15]" : "border-l-zinc-900";
  return (
    <div className={`hard-card p-4 border-l-4 ${colorClass}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">{label}</div>
      <div className="text-3xl font-display font-black mt-1">{value}</div>
    </div>
  );
}

function StatusPill({ status }) {
  const map = {
    new: "bg-blue-100 text-blue-900 border-blue-300",
    contacted: "bg-purple-100 text-purple-900 border-purple-300",
    converted: "bg-green-100 text-green-900 border-green-300",
    "do-not-contact": "bg-red-100 text-red-900 border-red-300",
  };
  const cls = map[status] || "bg-zinc-100 text-zinc-700 border-zinc-300";
  return <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}>{status || "new"}</span>;
}

/* ---------------- Search Google dialog ---------------- */
function SearchGoogleDialog({ onClose, onSaved }) {
  const [query, setQuery] = useState("civil construction");
  const [location, setLocation] = useState("Auckland");
  const [max, setMax] = useState(20);
  const [results, setResults] = useState(null);
  const [busy, setBusy] = useState(false);
  const [saved, setSaved] = useState(null);

  const run = async () => {
    setBusy(true);
    setResults(null);
    setSaved(null);
    try {
      const r = await api.post("/campaigns/leads/search", { query, location, max_results: max });
      setResults(r.data.results || []);
      setSaved(r.data.saved || 0);
      toast.success(`Found ${r.data.results.length} businesses, saved ${r.data.saved} new`);
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Search failed";
      if (typeof msg === "string" && msg.includes("PERMISSION_DENIED")) {
        toast.error("Places API (New) is not enabled in your Google Cloud project. Enable it at console.cloud.google.com → APIs & Services → Library.", { duration: 9000 });
      } else {
        toast.error(typeof msg === "string" ? msg : "Search failed");
      }
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaigns-search-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Search Google for businesses</DialogTitle>
          <DialogDescription>
            Uses Google Places (NZ-only). Each search counts against your Google Cloud free tier (~6,000 free/month).
            New leads are saved automatically (duplicates skipped).
          </DialogDescription>
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Search term</Label>
            <Input value={query} onChange={(e) => setQuery(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. drainlayer, earthmoving, civil contractor" data-testid="campaigns-search-query" />
          </div>
          <div>
            <Label className="label-tag">Location (NZ city/region)</Label>
            <Input value={location} onChange={(e) => setLocation(e.target.value)} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. Auckland, Wellington" data-testid="campaigns-search-location" />
          </div>
          <div>
            <Label className="label-tag">Max results (1-60)</Label>
            <Input type="number" min={1} max={60} value={max} onChange={(e) => setMax(Math.max(1, Math.min(60, Number(e.target.value) || 20)))} className="mt-2 rounded-sm border-zinc-400" data-testid="campaigns-search-max" />
          </div>
        </div>

        {results && (
          <div className="mt-4">
            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 mb-2">
              {results.length} result{results.length === 1 ? "" : "s"} · {saved} saved
            </div>
            <div className="max-h-72 overflow-y-auto border border-zinc-200">
              {results.map((r, i) => (
                <div key={i} className="px-3 py-2 border-b border-zinc-100 text-xs">
                  <div className="font-bold">{r.name}</div>
                  <div className="text-zinc-500">{r.address}</div>
                  {r.website && <a href={r.website} target="_blank" rel="noreferrer" className="text-[#FF5F15] hover:underline">{r.website}</a>}
                </div>
              ))}
            </div>
          </div>
        )}

        <DialogFooter className="gap-2">
          <Button type="button" variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Close</Button>
          <Button onClick={run} disabled={busy || !query.trim()} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-search-submit">
            <Search className="w-3.5 h-3.5 mr-2" /> {busy ? "Searching…" : "Search"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ManualLeadDialog({ onClose, onSaved }) {
  const [form, setForm] = useState({ name: "", website: "", phone: "", address: "", emails: "", notes: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!form.name.trim()) { toast.error("Name is required"); return; }
    setBusy(true);
    try {
      await api.post("/campaigns/leads", {
        name: form.name.trim(),
        website: form.website.trim() || null,
        phone: form.phone.trim() || null,
        address: form.address.trim() || null,
        emails: form.emails.split(/[,;\s]+/).filter((e) => e.includes("@")),
        notes: form.notes.trim() || null,
      });
      toast.success("Lead added");
      onSaved();
      onClose();
    } catch (e) {
      toast.error("Failed to save");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-lg" data-testid="campaigns-manual-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Add lead manually</DialogTitle>
        </DialogHeader>
        <div className="space-y-3">
          {[
            ["name", "Business name *", "Acme Drainage Ltd"],
            ["website", "Website", "https://acme.co.nz"],
            ["phone", "Phone", "021 123 456"],
            ["address", "Address", ""],
            ["emails", "Emails (comma-separated)", "info@acme.co.nz, sales@acme.co.nz"],
          ].map(([k, label, ph]) => (
            <div key={k}>
              <Label className="label-tag">{label}</Label>
              <Input value={form[k]} onChange={(e) => setForm({ ...form, [k]: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder={ph} data-testid={`campaigns-manual-${k}`} />
            </div>
          ))}
          <div>
            <Label className="label-tag">Notes</Label>
            <Textarea rows={2} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="campaigns-manual-notes" />
          </div>
        </div>
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-manual-save">
            Save lead
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ImportCsvDialog({ onClose, onSaved }) {
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const upload = async () => {
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const r = await api.post("/campaigns/leads/import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`Imported ${r.data.inserted}, skipped ${r.data.skipped}`);
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Import failed");
    } finally {
      setBusy(false);
    }
  };
  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-md" data-testid="campaigns-import-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">Import CSV</DialogTitle>
          <DialogDescription>
            CSV with columns (any subset): <code className="text-[11px] bg-zinc-100 px-1">name</code>, <code className="text-[11px] bg-zinc-100 px-1">website</code>, <code className="text-[11px] bg-zinc-100 px-1">email</code>, <code className="text-[11px] bg-zinc-100 px-1">phone</code>, <code className="text-[11px] bg-zinc-100 px-1">address</code>, <code className="text-[11px] bg-zinc-100 px-1">notes</code>
          </DialogDescription>
        </DialogHeader>
        <Input type="file" accept=".csv,text/csv" onChange={(e) => setFile(e.target.files?.[0] || null)} className="rounded-sm border-zinc-400" data-testid="campaigns-import-file" />
        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm" disabled={busy}>Cancel</Button>
          <Button onClick={upload} disabled={busy || !file} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-import-submit">
            {busy ? "Importing…" : "Import"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/* ============================================================
   CAMPAIGNS TAB
   ============================================================ */
function CampaignsTab() {
  const [items, setItems] = useState([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    try {
      const r = await api.get("/campaigns", { params: { audience_type: "leads", page } });
      setItems(r.data.items || []);
      setTotal(r.data.total || 0);
    } catch (e) {
      toast.error("Failed to load campaigns");
    }
  };
  useEffect(() => { load(); }, [page]);

  const openNew = () => { setEditing(null); setEditorOpen(true); };
  const openEdit = async (c) => { try { const { data } = await api.get(`/campaigns/${c.id}`); setEditing(data); setEditorOpen(true); } catch { toast.error("Could not load campaign"); } };

  const del = async (c) => {
    if (!confirm(`Delete campaign "${c.name}"?`)) return;
    await api.delete(`/campaigns/${c.id}`);
    load();
  };

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap gap-2 items-center justify-between">
        <div>
          <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-500">Email campaigns</div>
          <div className="text-sm text-zinc-600">Draft → preview → test send → review → confirmed send to selected leads.</div>
        </div>
        <Button onClick={openNew} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-new-btn">
          <Plus className="w-3.5 h-3.5 mr-2" /> New campaign
        </Button>
      </div>

      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaigns-list-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2">Campaign</th>
              <th className="px-3 py-2 hidden md:table-cell">Status</th>
              <th className="px-3 py-2 text-center">Sent</th>
              <th className="px-3 py-2 text-center">Opened</th>
              <th className="px-3 py-2 text-center">Clicked</th>
              <th className="px-3 py-2 text-center hidden md:table-cell">Unsub</th>
              <th className="px-3 py-2 hidden lg:table-cell">Updated</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && (
              <tr><td colSpan={8} className="px-3 py-10 text-center text-zinc-500">No campaigns yet.</td></tr>
            )}
            {items.map((c) => {
              const sent = c.stats?.sent || 0;
              const opens = c.stats?.opened || 0;
              const clicks = c.stats?.clicked || 0;
              const unsubs = c.stats?.unsubscribed || 0;
              const openPct = sent ? Math.round((opens / sent) * 100) : 0;
              const clickPct = sent ? Math.round((clicks / sent) * 100) : 0;
              return (
                <tr key={c.id} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`campaigns-row-${c.id}`}>
                  <td className="px-3 py-2">
                    <div className="font-bold">{c.name}</div>
                    <div className="text-[11px] text-zinc-500 mt-0.5 truncate max-w-md">{c.subject}</div>
                  </td>
                  <td className="px-3 py-2 hidden md:table-cell">
                    <CampaignStatus status={c.status} />
                  </td>
                  <td className="px-3 py-2 text-center font-mono text-xs">{sent}</td>
                  <td className="px-3 py-2 text-center font-mono text-xs">{opens} <span className="text-zinc-400">/{openPct}%</span></td>
                  <td className="px-3 py-2 text-center font-mono text-xs">{clicks} <span className="text-zinc-400">/{clickPct}%</span></td>
                  <td className="px-3 py-2 text-center font-mono text-xs hidden md:table-cell">{unsubs}</td>
                  <td className="px-3 py-2 hidden lg:table-cell text-[11px] text-zinc-500">{fmtDate(c.updated_at)}</td>
                  <td className="px-3 py-2 text-right">
                    <div className="inline-flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => openEdit(c)} className="rounded-sm text-xs h-7 px-2" data-testid={`campaigns-edit-${c.id}`}>
                        {c.status === "draft" ? "Edit" : "View"}
                      </Button>
                      <Button size="icon" variant="ghost" className="h-7 w-7 text-red-600" onClick={() => del(c)} data-testid={`campaigns-delete-${c.id}`}>
                        <Trash2 className="w-3.5 h-3.5" />
                      </Button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between"><Button disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Previous</Button><span>{total} campaigns · Page {page}</span><Button disabled={page * 30 >= total} onClick={() => setPage(p => p + 1)}>Next</Button></div>
      {editorOpen && (
        <CampaignEditor
          campaign={editing}
          onClose={() => setEditorOpen(false)}
          onSaved={() => { setEditorOpen(false); load(); }}
        />
      )}
    </div>
  );
}

function CampaignStatus({ status }) {
  const map = {
    draft: "bg-zinc-100 text-zinc-700 border-zinc-300",
    sending: "bg-blue-100 text-blue-900 border-blue-300",
    sent: "bg-green-100 text-green-900 border-green-300",
  };
  const cls = map[status] || map.draft;
  return <span className={`inline-flex items-center text-[10px] font-bold uppercase tracking-wider px-1.5 py-0.5 rounded-sm border ${cls}`}>{status || "draft"}</span>;
}

function CampaignEditor({ campaign, onClose, onSaved }) {
  const readOnly = campaign && campaign.status !== "draft";
  const [form, setForm] = useState({
    name: campaign?.name || "",
    subject: campaign?.subject || "",
    body_html: campaign?.body_html || DEFAULT_BODY,
    from_name: campaign?.from_name || "Fieldlink NZ",
    reply_to: campaign?.reply_to || "",
  });
  const [recipients, setRecipients] = useState(null);
  const [previewHtml, setPreviewHtml] = useState(null);
  const [testEmail, setTestEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [currentId, setCurrentId] = useState(campaign?.id || null);
  const [sendModalOpen, setSendModalOpen] = useState(false);
  const [leadsForSend, setLeadsForSend] = useState([]);
  const [selectedLeads, setSelectedLeads] = useState({});

  const save = async () => {
    if (!form.name.trim() || !form.subject.trim()) {
      toast.error("Name and subject are required");
      return;
    }
    setBusy(true);
    try {
      let id = currentId;
      if (id) {
        await api.patch(`/campaigns/${id}`, form);
      } else {
        const r = await api.post("/campaigns", form);
        id = r.data.id;
        setCurrentId(id);
      }
      toast.success("Saved");
      return id;
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
      return null;
    } finally {
      setBusy(false);
    }
  };

  const preview = async () => {
    const id = currentId || await save();
    if (!id) return;
    try {
      const r = await api.post(`/campaigns/${id}/preview`);
      setPreviewHtml(r.data.html);
    } catch (e) {
      toast.error("Preview failed");
    }
  };

  const sendTest = async () => {
    if (!testEmail.includes("@")) { toast.error("Enter a valid email"); return; }
    const id = currentId || await save();
    if (!id) return;
    setBusy(true);
    try {
      await api.post(`/campaigns/${id}/test`, { email: testEmail });
      toast.success(`Test sent to ${testEmail}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Test failed");
    } finally {
      setBusy(false);
    }
  };

  const openSendModal = async () => {
    const id = currentId || await save();
    if (!id) return;
    try {
      const r = await api.get("/campaigns/leads", { params: { with_email: true } });
      setLeadsForSend(r.data.items || []);
      const sel = {};
      (r.data.items || []).forEach((l) => { if (l.status !== "do-not-contact") sel[l.id] = true; });
      setSelectedLeads(sel);
      setSendModalOpen(true);
    } catch (e) {
      toast.error("Failed to load leads");
    }
  };

  const confirmSend = async () => {
    const id = currentId;
    const ids = Object.keys(selectedLeads).filter((k) => selectedLeads[k]);
    if (!ids.length) { toast.error("Pick at least one recipient"); return; }
    if (!confirm(`Send this campaign to ${ids.length} recipient${ids.length === 1 ? "" : "s"}? This cannot be undone.`)) return;
    setBusy(true);
    try {
      const r = await api.post(`/campaigns/${id}/send`, { lead_ids: ids, confirmed: true });
      toast.success(`Queued ${r.data.queued} sends. Tracking starts now.`);
      setSendModalOpen(false);
      onSaved();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Send failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="rounded-sm max-w-4xl max-h-[92vh] overflow-y-auto" data-testid="campaigns-editor-dialog">
        <DialogHeader>
          <DialogTitle className="font-display uppercase tracking-tight text-2xl">
            {readOnly ? "Campaign" : (currentId ? "Edit campaign" : "New campaign")}
          </DialogTitle>
          {readOnly && (
            <DialogDescription>This campaign has been sent and is read-only. Open-rate / click stats update live below.</DialogDescription>
          )}
        </DialogHeader>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <div>
            <Label className="label-tag">Campaign name *</Label>
            <Input value={form.name} disabled={readOnly} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="e.g. May NZ Civil Outreach" data-testid="campaigns-editor-name" />
          </div>
          <div>
            <Label className="label-tag">From name</Label>
            <Input value={form.from_name} disabled={readOnly} onChange={(e) => setForm({ ...form, from_name: e.target.value })} className="mt-2 rounded-sm border-zinc-400" data-testid="campaigns-editor-from" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tag">Subject *</Label>
            <Input value={form.subject} disabled={readOnly} onChange={(e) => setForm({ ...form, subject: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="Free time-tracking for your civil crew" data-testid="campaigns-editor-subject" />
          </div>
          <div className="sm:col-span-2">
            <Label className="label-tag">Reply-to (optional)</Label>
            <Input value={form.reply_to} disabled={readOnly} onChange={(e) => setForm({ ...form, reply_to: e.target.value })} className="mt-2 rounded-sm border-zinc-400" placeholder="sales@fieldlink.co.nz" data-testid="campaigns-editor-replyto" />
          </div>
          <div className="sm:col-span-2">
            <div className="flex items-center justify-between">
              <Label className="label-tag">Body (HTML)</Label>
              <button type="button" onClick={() => setForm({ ...form, body_html: DEFAULT_BODY })} disabled={readOnly} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-[#FF5F15]">
                Reset template
              </button>
            </div>
            <Textarea
              rows={12}
              value={form.body_html}
              disabled={readOnly}
              onChange={(e) => setForm({ ...form, body_html: e.target.value })}
              className="mt-2 rounded-sm border-zinc-400 font-mono text-xs"
              data-testid="campaigns-editor-body"
            />
            <div className="mt-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              We append the unsubscribe footer + tracking pixel automatically. Links inside your HTML are auto-wrapped for click tracking.
            </div>
          </div>
        </div>

        {previewHtml && (
          <div className="mt-2">
            <div className="flex items-center justify-between mb-1">
              <Label className="label-tag">Preview</Label>
              <button onClick={() => setPreviewHtml(null)} className="text-[10px] font-mono uppercase tracking-widest text-zinc-500 hover:text-zinc-900"><X className="w-3 h-3 inline" /> Close</button>
            </div>
            <iframe title="Email preview" srcDoc={previewHtml} className="w-full h-72 border border-zinc-300 bg-white" data-testid="campaigns-editor-preview" />
          </div>
        )}

        {!readOnly && (
          <div className="mt-4 pt-4 border-t border-zinc-200 space-y-3">
            <div className="flex flex-wrap items-center gap-2">
              <Input value={testEmail} onChange={(e) => setTestEmail(e.target.value)} placeholder="you@example.com" className="h-9 w-56 rounded-sm border-zinc-400 text-xs" data-testid="campaigns-editor-test-email" />
              <Button onClick={sendTest} disabled={busy} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-editor-test-btn">
                <Send className="w-3.5 h-3.5 mr-2" /> Send test
              </Button>
              <Button onClick={preview} disabled={busy} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-editor-preview-btn">
                <Eye className="w-3.5 h-3.5 mr-2" /> Preview
              </Button>
            </div>
          </div>
        )}

        {readOnly && currentId && (
          <RecipientsPanel campaignId={currentId} />
        )}

        <DialogFooter className="gap-2">
          <Button variant="outline" onClick={onClose} className="rounded-sm">Close</Button>
          {!readOnly && (
            <>
              <Button onClick={save} disabled={busy} variant="outline" className="rounded-sm uppercase tracking-wider text-xs font-bold" data-testid="campaigns-editor-save-btn">
                Save draft
              </Button>
              <Button onClick={openSendModal} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-editor-send-btn">
                <Send className="w-3.5 h-3.5 mr-2" /> Send campaign
              </Button>
            </>
          )}
        </DialogFooter>

        {sendModalOpen && (
          <Dialog open onOpenChange={(v) => { if (!v) setSendModalOpen(false); }}>
            <DialogContent className="rounded-sm max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="campaigns-send-confirm-dialog">
              <DialogHeader>
                <DialogTitle className="font-display uppercase tracking-tight text-2xl">Pick recipients</DialogTitle>
                <DialogDescription>
                  Unsubscribed addresses and &quot;do not contact&quot; leads are filtered out automatically. Each lead can have multiple emails — all of them will be sent.
                </DialogDescription>
              </DialogHeader>
              <div className="flex items-center gap-2 text-xs mb-2">
                <button onClick={() => { const all = {}; leadsForSend.forEach((l) => { if (l.status !== "do-not-contact") all[l.id] = true; }); setSelectedLeads(all); }} className="text-[#FF5F15] hover:underline">Select all</button>
                <span className="text-zinc-300">·</span>
                <button onClick={() => setSelectedLeads({})} className="text-[#FF5F15] hover:underline">Clear</button>
                <span className="text-zinc-300">·</span>
                <span className="text-zinc-500">{Object.values(selectedLeads).filter(Boolean).length} of {leadsForSend.length} selected</span>
              </div>
              <div className="border border-zinc-200 max-h-96 overflow-y-auto">
                {leadsForSend.length === 0 && (
                  <div className="px-3 py-6 text-center text-zinc-500 text-xs">No leads with email addresses yet. Go to the Leads tab.</div>
                )}
                {leadsForSend.map((l) => {
                  const dnc = l.status === "do-not-contact";
                  return (
                    <label key={l.id} className={`flex items-start gap-2 px-3 py-2 border-b border-zinc-100 text-xs ${dnc ? "opacity-40" : "hover:bg-zinc-50 cursor-pointer"}`}>
                      <input type="checkbox" disabled={dnc} checked={!!selectedLeads[l.id]} onChange={(e) => setSelectedLeads({ ...selectedLeads, [l.id]: e.target.checked })} className="mt-0.5" />
                      <div className="flex-1 min-w-0">
                        <div className="font-bold">{l.name}</div>
                        <div className="text-zinc-500 truncate">{(l.emails || []).join(", ")}</div>
                      </div>
                      {dnc && <span className="text-[9px] font-bold uppercase tracking-wider text-red-600">DNC</span>}
                    </label>
                  );
                })}
              </div>
              <DialogFooter className="gap-2">
                <Button variant="outline" onClick={() => setSendModalOpen(false)} className="rounded-sm" disabled={busy}>Cancel</Button>
                <Button onClick={confirmSend} disabled={busy} className="rounded-sm bg-[#FF5F15] hover:bg-[#E65100] text-white uppercase tracking-wider text-xs font-bold" data-testid="campaigns-send-confirm-btn">
                  <Send className="w-3.5 h-3.5 mr-2" /> {busy ? "Queuing…" : "Send now"}
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        )}
      </DialogContent>
    </Dialog>
  );
}

function RecipientsPanel({ campaignId }) {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get(`/campaigns/${campaignId}/recipients`);
      setItems(r.data.items || []);
    } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [campaignId]);

  return (
    <div className="mt-4 pt-4 border-t border-zinc-200">
      <div className="flex items-center justify-between mb-2">
        <Label className="label-tag">Recipients & tracking</Label>
        <Button size="sm" variant="ghost" onClick={load} className="rounded-sm text-xs h-7" data-testid="campaigns-recipients-refresh">
          <RefreshCw className="w-3 h-3 mr-1" /> Refresh
        </Button>
      </div>
      <div className="border border-zinc-200 max-h-72 overflow-y-auto">
        {loading && <div className="px-3 py-4 text-center text-xs text-zinc-500">Loading…</div>}
        {!loading && items.length === 0 && <div className="px-3 py-4 text-center text-xs text-zinc-500">No sends yet.</div>}
        {items.map((r) => (
          <div key={r.id} className="px-3 py-2 border-b border-zinc-100 text-xs flex items-center gap-3" data-testid={`campaigns-recipient-${r.id}`}>
            <div className="flex-1 min-w-0">
              <div className="font-bold truncate">{r.lead_name || "—"}</div>
              <div className="text-zinc-500 truncate">{r.email}</div>
            </div>
            <div className="flex items-center gap-2 text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              {r.status === "sent" && <span className="text-green-700">Sent</span>}
              {r.status === "failed" && <span className="text-red-600">Failed</span>}
              {r.status === "queued" && <span className="text-blue-600">Queued</span>}
              {r.opened_at && <span className="inline-flex items-center gap-1 text-green-700"><Eye className="w-3 h-3" />{r.open_count}</span>}
              {r.clicked_at && <span className="inline-flex items-center gap-1 text-[#FF5F15]"><MousePointerClick className="w-3 h-3" />{r.click_count}</span>}
              {r.unsubscribed_at && <span className="text-red-600">Unsub</span>}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

/* ============================================================
   SUPPRESSION TAB
   ============================================================ */
function SuppressionTab() {
  const [items, setItems] = useState([]);
  const load = async () => {
    try {
      const r = await api.get("/campaigns/suppression");
      setItems(r.data.items || []);
    } catch { toast.error("Failed to load"); }
  };
  useEffect(() => { load(); }, []);
  const remove = async (email) => {
    if (!confirm(`Remove ${email} from suppression list? They'll be allowed to receive future campaigns.`)) return;
    await api.delete(`/campaigns/suppression/${encodeURIComponent(email)}`);
    load();
  };
  return (
    <div className="space-y-3">
      <div className="text-sm text-zinc-600">
        Email addresses on this list will not receive any campaign emails. They got here by clicking the unsubscribe link in one of your emails.
      </div>
      <div className="hard-card overflow-x-auto">
        <table className="w-full text-sm" data-testid="campaigns-suppression-table">
          <thead className="bg-zinc-50 border-b border-zinc-200">
            <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500">
              <th className="px-3 py-2">Email</th>
              <th className="px-3 py-2">Reason</th>
              <th className="px-3 py-2">When</th>
              <th className="px-3 py-2 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <tr><td colSpan={4} className="px-3 py-10 text-center text-zinc-500">No unsubscribes.</td></tr>}
            {items.map((s) => (
              <tr key={s.email} className="border-b border-zinc-100 hover:bg-zinc-50" data-testid={`campaigns-suppression-row-${s.email}`}>
                <td className="px-3 py-2 font-mono">{s.email}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">{s.reason}</td>
                <td className="px-3 py-2 text-xs text-zinc-500">{fmtDate(s.created_at)}</td>
                <td className="px-3 py-2 text-right">
                  <Button size="sm" variant="outline" onClick={() => remove(s.email)} className="rounded-sm text-xs h-7" data-testid={`campaigns-suppression-remove-${s.email}`}>
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

const DEFAULT_BODY = `<div style="font-family:Helvetica,Arial,sans-serif;max-width:560px;margin:0 auto;padding:24px;color:#09090B;">
  <h2 style="margin:0 0 14px;font-size:24px;letter-spacing:-0.5px;line-height:1.2;">
    Still chasing timesheets on the back of a smoko packet?
  </h2>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;">
    Kia ora — quick one.
  </p>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;">
    I built <a href="https://www.fieldlink.co.nz" style="color:#FF5F15;text-decoration:none;font-weight:700;">Fieldlink NZ</a> because I got sick of watching good civil crews drown in paperwork while the real work happened in the trench.
  </p>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;font-weight:700;margin-top:18px;">
    It's basically a smoko-break for your office:
  </p>

  <ul style="font-size:14px;line-height:1.75;color:#3f3f46;padding-left:18px;margin:6px 0 0;">
    <li>📋 <b>Timesheets</b> the lads actually fill in (works offline, even on a dusty iPhone in the bottom of a dig)</li>
    <li>🦺 <b>SSSPs, toolbox talks, hazard reports</b> — done on-site in 90 seconds, not at the kitchen table on Sunday night</li>
    <li>🚛 <b>Fleet, WoF, RUC + live Cartrack tracking</b> — finally know which ute is where without ringing five blokes</li>
    <li>💸 <b>Quotes, variations, invoices</b> — get paid before the next pay run, not three months after the job's backfilled</li>
  </ul>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;margin-top:22px;">
    Built in NZ, for NZ. No American jargon, no "synergy", no monthly contract — just software that works as hard as your crew does.
  </p>

  <table cellpadding="0" cellspacing="0" border="0" style="margin:22px 0;">
    <tr>
      <td bgcolor="#FF5F15" style="padding:14px 28px;">
        <a href="https://www.fieldlink.co.nz" style="color:#ffffff;text-decoration:none;font-family:Helvetica,Arial,sans-serif;font-size:13px;font-weight:bold;letter-spacing:1.5px;text-transform:uppercase;">
          Have a squiz → fieldlink.co.nz
        </a>
      </td>
    </tr>
  </table>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;">
    Reply <b>"send it"</b> and I'll flick you a free 14-day trial — no card, no sales call, no BS.
  </p>

  <p style="font-size:14px;line-height:1.6;color:#3f3f46;margin-top:20px;">
    Nga mihi,<br/>
    <b>The Fieldlink crew</b><br/>
    <span style="color:#71717a;font-size:12px;">P.S. If your current "system" is a clipboard, a group chat and a prayer — we should definitely talk.</span>
  </p>
</div>`;
